import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getUserTimezone } from "../../modules/timezone";
import { MeetingType } from "../../services/meetingTypeService";
import {
  TimeSlot,
  BookingFormData,
  BookingState,
  CachedSlotData,
} from "../../types/booking";
import { FormTemplate } from "../../services/formTemplateService";

/**
 * Phase 3 — Pay Now. Set when POST /api/bookings returns
 * { paymentRequired: true, payment: {...} } and cleared when payment
 * succeeds (or the invitee abandons and the slot expires).
 */
export interface PaymentInfo {
  bookingId: number;
  clientSecret: string;
  publishableKey: string;
  connectedAccountId: string;
  expiresAt: string;
  amount: number;
  currency: string;
}

// Define the context type
interface BookingPageContextType {
  // Meeting type data
  meetingType: MeetingType | null;
  setMeetingType: (meetingType: MeetingType | null) => void;

  // Loading and error states
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;

  // Date and time selection
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedTime: TimeSlot | null;
  setSelectedTime: (time: TimeSlot | null) => void;

  // Timezone selection
  selectedTimezone: string;
  setSelectedTimezone: (timezone: string) => void;

  // Booking form data
  bookingForm: BookingFormData;
  setBookingForm: (form: BookingFormData) => void;
  updateBookingForm: (field: string, value: string) => void;

  // Submission state
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;

  // Booking success and details
  bookingSuccess: boolean;
  setBookingSuccess: (success: boolean) => void;
  bookingDetails: any;
  setBookingDetails: (details: any) => void;

  // Pay Now (Phase 3 host payment collection)
  paymentInfo: PaymentInfo | null;
  setPaymentInfo: (info: PaymentInfo | null) => void;

  // Available slots management
  availableSlots: TimeSlot[];
  setAvailableSlots: (slots: TimeSlot[]) => void;
  loadingSlots: boolean;
  setLoadingSlots: (loading: boolean) => void;

  // Calendar connection state
  calendarConnected: boolean;
  setCalendarConnected: (connected: boolean) => void;

  // Cache state
  cachedMonthSlots: Record<string, CachedSlotData>;
  setCachedMonthSlots: (slots: Record<string, CachedSlotData>) => void;

  // Form template data
  formTemplate: FormTemplate | null;
  setFormTemplate: (template: FormTemplate | null) => void;
  loadingFormTemplate: boolean;
  setLoadingFormTemplate: (loading: boolean) => void;
}

// Create the context
const BookingPageContext = createContext<BookingPageContextType | undefined>(
  undefined
);

// Provider component
export const BookingPageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Meeting type data
  const [meetingType, setMeetingType] = useState<MeetingType | null>(null);

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Date and time selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);

  // FIXED: Timezone selection - ALWAYS initialize with user's browser timezone
  // Users can manually switch to meeting type timezone if they prefer
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    getUserTimezone()
  );

  // Use a ref to track the previous timezone to avoid unnecessary updates
  const prevTimezoneRef = useRef<string>(selectedTimezone);

  // Booking form data with initial empty state
  const [bookingForm, setBookingForm] = useState<BookingFormData>({
    name: "",
    email: "",
    notes: "",
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Booking success and details
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);

  // Pay Now (Phase 3). null = no payment needed / already paid.
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);

  // Available slots management
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);

  // Calendar connection state
  const [calendarConnected, setCalendarConnected] = useState<boolean>(false);

  // Cache state - updated to use CachedSlotData
  const [cachedMonthSlots, setCachedMonthSlots] = useState<
    Record<string, CachedSlotData>
  >({});

  // Form template state
  const [formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);
  const [loadingFormTemplate, setLoadingFormTemplate] = useState<boolean>(false);

  // FIXED: Do NOT override user's timezone with meeting type timezone
  // The booking page should always respect the user's browser timezone
  // Users can manually switch timezone using the timezone selector if needed
  useEffect(() => {
    if (meetingType) {
      // Check if calendar is connected
      setCalendarConnected(!!meetingType.calendarConnection);

      // Timezone is available from meetingType.timeZone for user reference
    }
  }, [meetingType]);

  // Effect to track timezone changes
  // Note: Cache clearing is now handled by BookingDataProvider.switchTimezone
  useEffect(() => {
    // Only run this effect if the timezone has actually changed
    if (prevTimezoneRef.current !== selectedTimezone) {

      // Clear selected time when timezone changes
      if (selectedTime) {
        setSelectedTime(null);
      }

      // Update the ref with the current timezone
      prevTimezoneRef.current = selectedTimezone;
    }
  }, [selectedTimezone]); // Remove selectedTime from dependency array

  // Memoize the updateBookingForm function to prevent unnecessary re-renders
  const updateBookingForm = useCallback((field: string, value: string) => {
    setBookingForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // Meeting type data
      meetingType,
      setMeetingType,

      // Loading and error states
      loading,
      setLoading,
      error,
      setError,

      // Date and time selection
      selectedDate,
      setSelectedDate,
      selectedTime,
      setSelectedTime,

      // Timezone selection
      selectedTimezone,
      setSelectedTimezone,

      // Booking form data
      bookingForm,
      setBookingForm,
      updateBookingForm,

      // Submission state
      isSubmitting,
      setIsSubmitting,

      // Booking success and details
      bookingSuccess,
      setBookingSuccess,
      bookingDetails,
      setBookingDetails,

      // Pay Now
      paymentInfo,
      setPaymentInfo,

      // Available slots management
      availableSlots,
      setAvailableSlots,
      loadingSlots,
      setLoadingSlots,

      // Calendar connection state
      calendarConnected,
      setCalendarConnected,

      // Cache state
      cachedMonthSlots,
      setCachedMonthSlots,

      // Form template data
      formTemplate,
      setFormTemplate,
      loadingFormTemplate,
      setLoadingFormTemplate,
    }),
    [
      meetingType,
      loading,
      error,
      selectedDate,
      selectedTime,
      selectedTimezone,
      bookingForm,
      updateBookingForm,
      isSubmitting,
      bookingSuccess,
      bookingDetails,
      paymentInfo,
      availableSlots,
      loadingSlots,
      calendarConnected,
      cachedMonthSlots,
      formTemplate,
      loadingFormTemplate,
    ]
  );

  return (
    <BookingPageContext.Provider value={contextValue}>
      {children}
    </BookingPageContext.Provider>
  );
};

// Custom hook to use the booking page context
export const useBookingPage = () => {
  const context = useContext(BookingPageContext);
  if (!context) {
    throw new Error("useBookingPage must be used within a BookingPageProvider");
  }
  return context;
};
